import { useEffect, useState, useMemo, useRef } from "react";
import { Plus, X, CalendarIcon, Check, ArchiveRestore } from "lucide-react";
import PillSelect from "@/components/common/PillSelect";
import { StagePill, PILL_FIXED } from "@/components/leads/LeadPipelineBoard";
import { LeadNotesCell, leadNotes } from "@/components/leads/LeadNotesCell";
import { STAGES, TEMPERATURES, isStale, isOpenStage, stageMeta } from "@/components/leads/pipeline";
import { TempDot } from "@/components/leads/TempDot";
import { useNavigate, useLocation } from "react-router-dom";
import { getLeads, createLead, updateLead, convertLead, convertLeadToProposal, leadCustomerSearch, updateLeadStatus, updateLeadTemperature, updateLeadFollowUp, getLeadSources, createLeadSource, getAccountManagers, createAccountManager } from "@/lib/api";
import { formatPhoneNumber } from "@/lib/phoneFormat";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { toast } from "sonner";
import { format } from "date-fns";
import SearchBar from "@/components/common/SearchBar";
import { PillRail, Pill, PillDivider } from "@/components/common/Segmented";
import StickyTop from "@/components/common/StickyTop";

import { SortableHeader, HeaderFilter } from "@/components/common/SortHeader";
import { btn, BTN } from "@/components/common/btn";
import { shortDate, money } from "@/lib/format";
import { activePeople, assignablePeople } from "@/lib/roster";

// iter343 — pipeline stages (Cold/Warm/Hot live on lead.temperature now)
const statusOptions = [
  ...STAGES.map(s => ({ value: s.value, label: s.label })),
  { value: "won", label: "Won" },
  { value: "converted", label: "Converted" },
  { value: "lost", label: "Lost" },
];

// Column header: name sorts, click again flips; arrow only on the ruling column. Module-level so the header
// (and any filter popover inside it) does not remount on every page render.
const ColHeader = ({ field, children, filter, testId, sortField, sortDirection, onSort }) => (
  <SortableHeader label={children} sorted={sortField === field} direction={sortDirection} onSort={() => onSort(field)} filter={filter} testId={testId} />
);

const getLatestNoteText = (notes) => {
  if (Array.isArray(notes) && notes.length > 0) return notes[notes.length - 1].text || "";
  if (typeof notes === "string") return notes;
  return "";
};

// Won + Lost leads in one list. Status column tells them apart; Reopen puts either back in the pipeline.
function ArchivedLeadsTable({ rows, navigate, onRestore }) {
  if (!rows.length) return <div className="text-center py-10 text-slate-500 text-sm" data-testid="archived-leads-view">No archived leads</div>;
  return (
    <div data-testid="archived-leads-view">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="whitespace-nowrap text-xs font-semibold">Lead</TableHead>
            <TableHead className="w-[100px] whitespace-nowrap text-xs font-semibold">Status</TableHead>
            <TableHead className="whitespace-nowrap text-xs font-semibold">Customer</TableHead>
            <TableHead className="whitespace-nowrap text-xs font-semibold">Contact</TableHead>
            <TableHead className="w-[110px] whitespace-nowrap text-xs font-semibold num">Est. Value</TableHead>
            <TableHead className="whitespace-nowrap text-xs font-semibold">Source</TableHead>
            <TableHead className="whitespace-nowrap text-xs font-semibold">Notes</TableHead>
            <TableHead className="w-20"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((lead) => {
            const won = lead._outcome === "won";
            const linkedClientId = lead.client_id || lead.converted_client_id;
            return (
              <TableRow key={lead.id} className={`table-row-hover ${won ? "text-slate-600" : "text-slate-500"}`} data-testid={`${won ? "won" : "archived"}-lead-${lead.id}`}>
                <TableCell className="max-w-[320px] truncate text-xs font-medium">{lead.title || lead.company_name}</TableCell>
                <TableCell>
                  <span className={`${PILL_FIXED} ${won ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`} data-testid={`archived-lead-status-${lead.id}`}>
                    {won ? "Won" : "Lost"}
                  </span>
                </TableCell>
                <TableCell className="max-w-[240px]">
                  {linkedClientId ? (
                    <button type="button" className="block max-w-full truncate text-left text-xs text-slate-600 hover:text-brand hover:underline" onClick={() => navigate("/clients", { state: { openClientId: linkedClientId } })} data-testid={`won-lead-open-customer-${lead.id}`}>
                      {lead.client_name || lead.company_name}
                    </button>
                  ) : <span className="text-xs text-slate-300">—</span>}
                </TableCell>
                <TableCell className="max-w-[220px]">
                  <div className="truncate text-xs text-slate-700">{lead.contact_name || <span className="text-slate-300">—</span>}</div>
                  <div className="truncate text-[10px] text-slate-400">{[lead.email, lead.phone].filter(Boolean).join(" | ")}</div>
                </TableCell>
                <TableCell className="text-xs font-medium tabular-nums whitespace-nowrap">{money(lead.estimated_value)}</TableCell>
                <TableCell className="text-xs text-slate-600">{lead.source || <span className="text-slate-300">—</span>}</TableCell>
                <TableCell className="text-xs text-slate-600 max-w-[200px] truncate">{getLatestNoteText(lead.notes) || <span className="text-slate-300">—</span>}</TableCell>
                <TableCell>
                  <button type="button" className={btn("mode")} title="Put this lead back in the open pipeline" onClick={() => onRestore(lead)} data-testid={`restore-${won ? "won-" : ""}lead-${lead.id}`}>
                    <ArchiveRestore className="w-3.5 h-3.5" /> Reopen
                  </button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export default function Leads() {
  const navigate = useNavigate();
  const location = useLocation();
  const [leads, setLeads] = useState([]);
  const [archivedLeads, setArchivedLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [leadSources, setLeadSources] = useState([]);
  const [isAddingSource, setIsAddingSource] = useState(false);
  const [newSourceName, setNewSourceName] = useState("");
  const [accountManagers, setAccountManagers] = useState([]);
  const [amFilter, setAmFilter] = useState("all");
  const [isAddingAM, setIsAddingAM] = useState(false);
  const [newAMName, setNewAMName] = useState("");
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [wonConfirm, setWonConfirm] = useState(null);
  const [lostConfirm, setLostConfirm] = useState(null);
  const [formData, setFormData] = useState({
    company_name: "",
    title: "",
    contact_name: "",
    email: "",
    phone: "",
    status: "new",
    temperature: "",
    source: "",
    estimated_value: 0,
    probability: 20,
    notes: "",
    address: "",
    city: "",
    state: "",
    follow_up_date: null,
    follow_up_note: "",
    immediate_action: false,
    account_manager: "",
    // iter263 — set to an existing customer's id when the user picks
    // one from the autocomplete; stays null for prospect leads.
    client_id: null,
  });
  // iter263 — customer autocomplete state.
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [customerSuggestionsOpen, setCustomerSuggestionsOpen] = useState(false);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const customerSearchTimeoutRef = useRef(null);
  const [wonLeads, setWonLeads] = useState([]);
  const [showArchive, setShowArchive] = useState(false);
  const [staleOnly, setStaleOnly] = useState(false);

  useEffect(() => {
    loadLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, search]);

  useEffect(() => {
    loadLeadSources();
    loadAccountManagers();
  }, []);

  const loadLeads = async () => {
    try {
      const params = {};
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      else params.status = "open"; // excludes won, converted & lost
      if (search) params.search = search;
      // iter263 — pull both archive buckets in parallel (Won + Lost)
      const [response, lostRes, wonRes] = await Promise.all([
        getLeads(params),
        getLeads({ status: "lost", ...(search ? { search } : {}) }),
        getLeads({ status: "won", ...(search ? { search } : {}) }),
      ]);
      setLeads(response.data);
      setArchivedLeads(lostRes.data);
      setWonLeads(wonRes.data);
    } catch (error) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  const loadLeadSources = async () => {
    try {
      const response = await getLeadSources();
      setLeadSources(response.data);
    } catch (error) {
      console.error("Failed to load lead sources");
    }
  };

  const loadAccountManagers = async () => {
    try {
      const response = await getAccountManagers();
      setAccountManagers(response.data);
    } catch (error) {
      console.error("Failed to load account managers");
    }
  };

  const handleAddAM = async () => {
    if (!newAMName.trim()) return;
    try {
      await createAccountManager({ name: newAMName.trim() });
      setNewAMName("");
      setIsAddingAM(false);
      loadAccountManagers();
      toast.success("Account manager added");
    } catch { toast.error("Failed to add"); }
  };

  // iter263 — debounced customer search for the Lead form autocomplete.
  const handleCustomerNameChange = (val) => {
    setFormData(f => ({ ...f, company_name: val, client_id: null }));
    setCustomerSuggestionsOpen(true);
    if (customerSearchTimeoutRef.current) {
      clearTimeout(customerSearchTimeoutRef.current);
    }
    if ((val || "").trim().length < 2) {
      setCustomerSuggestions([]);
      return;
    }
    setCustomerSearchLoading(true);
    customerSearchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await leadCustomerSearch(val, 8);
        setCustomerSuggestions(res.data || []);
      } catch {
        setCustomerSuggestions([]);
      } finally {
        setCustomerSearchLoading(false);
      }
    }, 200);
  };

  // Pick an existing customer from the autocomplete → pre-fill contact
  // + address so the AM doesn't retype anything, and stamp client_id.
  const handlePickExistingCustomer = (customer) => {
    setFormData(f => ({
      ...f,
      company_name: customer.name || f.company_name,
      contact_name: f.contact_name || customer.contact_name || "",
      email: f.email || customer.contact_email || "",
      phone: f.phone || customer.contact_phone || customer.phone || "",
      address: f.address || customer.address || "",
      city: f.city || customer.city || "",
      state: f.state || customer.state || "",
      client_id: customer.id,
    }));
    setCustomerSuggestions([]);
    setCustomerSuggestionsOpen(false);
  };

  const handleClearExistingCustomer = () => {
    setFormData(f => ({ ...f, client_id: null }));
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Open (board / table) is the default list; Archived (won + lost) is the view outside it.
  const listView = showArchive ? "archived" : "open";
  const setListView = (v) => setShowArchive(v === "archived");
  const archivedRows = useMemo(() => [
    ...wonLeads.map((l) => ({ ...l, _outcome: "won" })),
    ...archivedLeads.map((l) => ({ ...l, _outcome: "lost" })),
  ].sort((a, b) => String(b.updated_at || b.created_at || "").localeCompare(String(a.updated_at || a.created_at || ""))), [wonLeads, archivedLeads]);
  const sortedLeads = useMemo(() => {
    let filtered = listView === "archived" ? archivedRows.filter(l => statusFilter === "all" || l.status === statusFilter) : leads;
    if (amFilter && amFilter !== "all") {
      filtered = filtered.filter(l => (l.account_manager || "").toLowerCase() === amFilter.toLowerCase());
    }
    if (staleOnly) filtered = filtered.filter(isStale);
    if (!sortField) return filtered;
    return [...filtered].sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case "company_name": aVal = (a.company_name || "").toLowerCase(); bVal = (b.company_name || "").toLowerCase(); break;
        case "contact_name": aVal = (a.contact_name || "").toLowerCase(); bVal = (b.contact_name || "").toLowerCase(); break;
        case "status": aVal = a.status || ""; bVal = b.status || ""; break;
        case "temperature": { const rank = { hot: 3, warm: 2, cold: 1 }; aVal = rank[a.temperature] || 0; bVal = rank[b.temperature] || 0; break; }
        case "estimated_value": aVal = a.estimated_value || 0; bVal = b.estimated_value || 0; break;
        case "source": aVal = (a.source || "").toLowerCase(); bVal = (b.source || "").toLowerCase(); break;
        case "account_manager": aVal = (a.account_manager || "").toLowerCase(); bVal = (b.account_manager || "").toLowerCase(); break;
        case "notes": aVal = getLatestNoteText(a.notes).toLowerCase(); bVal = getLatestNoteText(b.notes).toLowerCase(); break;
        case "created_at": aVal = a.created_at || ""; bVal = b.created_at || ""; break;
        case "last_activity_at": aVal = a.last_activity_at || a.updated_at || ""; bVal = b.last_activity_at || b.updated_at || ""; break;
        default: return 0;
      }
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [leads, listView, archivedRows, statusFilter, sortField, sortDirection, amFilter, staleOnly]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      if (!submitData.temperature) submitData.temperature = null;
      // Extract follow-up fields
      const followUpDate = submitData.follow_up_date;
      const followUpNote = submitData.follow_up_note;
      delete submitData.follow_up_date;
      delete submitData.follow_up_note;
      // Set follow-up fields on the lead
      submitData.next_follow_up = followUpDate ? followUpDate.toISOString() : null;
      submitData.follow_up_notes = followUpNote || "";
      submitData.follow_up_complete = false;
      // Notes: on edit the textarea is an "add a note" box — the existing log is always kept.
      const today = new Date().toISOString().slice(0, 10);
      const noteText = typeof submitData.notes === "string" ? submitData.notes.trim() : "";
      const existingNotes = editingLead ? leadNotes(editingLead) : [];
      submitData.notes = noteText ? [...existingNotes, { date: today, text: noteText }] : existingNotes;
      if (editingLead) {
        await updateLead(editingLead.id, submitData);
        toast.success("Lead updated successfully");
      } else {
        await createLead(submitData);
        toast.success("Lead created successfully");
      }
      setIsDialogOpen(false);
      resetForm();
      loadLeads();
    } catch (error) {
      toast.error("Failed to save lead");
    }
  };

  // iter243 — open a lead deep-linked from the Cmd+K palette.
  useEffect(() => {
    const id = location.state?.openLeadId;
    if (!id) return;
    const found = leads.find(l => l.id === id) || archivedLeads.find(l => l.id === id) || wonLeads.find(l => l.id === id);
    if (found) {
      handleEdit(found);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.openLeadId, leads, archivedLeads, wonLeads]);

  const handleEdit = (lead) => {
    setEditingLead(lead);
    setFormData({
      company_name: lead.company_name,
      title: lead.title || "",
      contact_name: lead.contact_name,
      email: lead.email || "",
      phone: lead.phone || "",
      status: lead.status,
      temperature: lead.temperature || "",
      source: lead.source || "",
      estimated_value: lead.estimated_value || 0,
      probability: lead.probability || 20,
      notes: "",
      address: lead.address || "",
      city: lead.city || "",
      state: lead.state || "",
      follow_up_date: lead.next_follow_up ? new Date(lead.next_follow_up) : null,
      follow_up_note: lead.follow_up_notes || "",
      immediate_action: lead.immediate_action || false,
      account_manager: lead.account_manager || "",
      // iter263 — hydrate the existing-customer link when editing.
      client_id: lead.client_id || null,
    });
    setIsDialogOpen(true);
  };

  const handleInlineStatusChange = async (leadId, newStatus) => {
    try {
      await updateLeadStatus(leadId, newStatus);
      toast.success("Status updated");
      loadLeads();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleInlineTempChange = async (leadId, temperature) => {
    try {
      await updateLeadTemperature(leadId, temperature);
      toast.success("Temperature updated");
      loadLeads();
    } catch { toast.error("Failed to update temperature"); }
  };

  const [convertDialog, setConvertDialog] = useState(null);
  const [convertSiteName, setConvertSiteName] = useState("Main Office");

  const openConvertDialog = (lead) => {
    setConvertDialog(lead);
    setConvertSiteName("Main Office");
  };

  // Create the customer record for a lead that doesn't have one yet (legacy leads). Stage is untouched.
  const handleConvert = async () => {
    if (!convertDialog) return;
    try {
      await convertLead(convertDialog.id, { site_name: convertSiteName });
      setConvertDialog(null);
      toast.success("Customer created — now on the customer list");
      loadLeads();
    } catch (error) {
      toast.error("Failed to create customer");
    }
  };

  // Won = the job is a go: draft the proposal (creates the customer if needed), archive the lead, open the proposal.
  const handleMarkWon = async (lead) => {
    try {
      const r = await convertLeadToProposal(lead.id, { won: true });
      setWonConfirm(null);
      toast.success(r.data.created ? "Lead won — proposal created" : "Lead won — opening its proposal");
      navigate("/proposals", { state: { viewProposalId: r.data.proposal_id } });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark won");
    }
  };

  const hasCustomer = (lead) => !!(lead.client_id || lead.converted_client_id);

  const handleLost = async (id) => {
    try {
      await updateLeadStatus(id, "lost");
      setLostConfirm(null);
      toast.success("Lead marked as lost");
      loadLeads();
    } catch (error) {
      toast.error("Failed to update lead status");
    }
  };

  const handleFollowUpComplete = async (lead) => {
    try {
      await updateLeadFollowUp(lead.id, {
        next_follow_up: lead.next_follow_up,
        follow_up_notes: lead.follow_up_notes,
        follow_up_complete: true,
      });
      setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, follow_up_complete: true } : l));
      toast.success("Follow-up marked complete");
    } catch (error) {
      toast.error("Failed to update follow-up");
    }
  };

  const handleAddSource = async () => {
    if (newSourceName.trim()) {
      try {
        const response = await createLeadSource({ name: newSourceName.trim() });
        setLeadSources([...leadSources, response.data]);
        setFormData({ ...formData, source: response.data.name });
        setNewSourceName("");
        setIsAddingSource(false);
        toast.success("Source added");
      } catch (error) {
        toast.error("Source already exists");
      }
    }
  };

  const resetForm = () => {
    setEditingLead(null);
    setFormData({
      company_name: "",
      title: "",
      contact_name: "",
      email: "",
      phone: "",
      status: "new",
      temperature: "",
      source: "",
      estimated_value: 0,
      probability: 20,
      notes: "",
      address: "",
      city: "",
      state: "",
      follow_up_date: null,
      follow_up_note: "",
      immediate_action: false,
      // Auto-fill the account manager when the signed-in user is an AM.
      // `abs_current_user` is set ONLY when the email matches an AM
      // record (see lib/currentUser.js → resolveCurrentUser), so no
      // mis-attribution risk for non-AM staff.
      account_manager: localStorage.getItem("abs_current_user") || "",
      // iter263 — clear existing-customer link when resetting.
      client_id: null,
    });
    setCustomerSuggestions([]);
    setCustomerSuggestionsOpen(false);
    setIsAddingSource(false);
  };

  const hdr = { sortField, sortDirection, onSort: handleSort };
  const restoreLead = async (lead) => {
    try { await updateLeadStatus(lead.id, "new"); toast.success("Lead restored"); loadLeads(); }
    catch { toast.error("Failed to restore"); }
  };
  const viewRows = sortedLeads;
  const viewTotal = viewRows.reduce((s, l) => s + (Number(l.estimated_value) || 0), 0);
  const staleCount = leads.filter(isStale).length;

  return (
    <div data-testid="leads-page" className="flex flex-col gap-2">
      <StickyTop testId="leads-sticky-top">
      <div className="flex shrink-0 flex-wrap items-center gap-2" data-testid="leads-toolbar">
        <SearchBar value={search} onChange={setSearch} testId="search-leads" width="w-64" />

        <PillRail testId="leads-view-toggle">
          <span className="inline-flex items-center gap-1" data-testid="filter-am-toolbar">
            <Pill on={amFilter === "all"} testId="am-pill-all" title="Every account manager's leads"
              onClick={() => setAmFilter("all")}>All</Pill>
            {activePeople(accountManagers).map(am => (
              <Pill key={am.id} on={amFilter === am.name} testId={`am-pill-${am.id}`}
                title={amFilter === am.name ? "Showing only this account manager — press again for everyone" : `Show only ${am.name}'s leads`}
                onClick={() => setAmFilter(amFilter === am.name ? "all" : am.name)}>
                {am.name.split(" ")[0]}
              </Pill>
            ))}
          </span>
          <span className="inline-flex items-center gap-1">
            <Pill on={staleOnly} testId="leads-stale-filter" title={`Open leads with no activity in 30+ days${staleCount ? ` (${staleCount})` : ""} — press again to show all`}
              onClick={() => { setStaleOnly(!staleOnly); setListView("open"); }}>Stale</Pill>
            <PillDivider />
            <Pill on={listView === "archived"} testId="toggle-archived-leads" title={listView === "archived" ? "Back to open leads" : "Show won and lost leads"}
              onClick={() => { setListView(listView === "archived" ? "open" : "archived"); setStaleOnly(false); setStatusFilter("all"); }}>Archived</Pill>
          </span>
        </PillRail>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <button type="button" onClick={resetForm} className={`${btn("write")} px-2`} title="Add a new lead" aria-label="Add a new lead" data-testid="btn-add-lead">
                  <Plus className="w-4 h-4" />
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {editingLead ? "Edit Lead" : "Add New Lead"}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="form-grid">
                    <div className="relative">
                      <Label htmlFor="company_name">Customer Name *</Label>
                      <Input
                        id="company_name"
                        value={formData.company_name}
                        onChange={(e) => handleCustomerNameChange(e.target.value)}
                        onFocus={() => { if ((formData.company_name || "").length >= 2) setCustomerSuggestionsOpen(true); }}
                        onBlur={() => { setTimeout(() => setCustomerSuggestionsOpen(false), 200); }}
                        required
                        autoComplete="off"
                        data-testid="input-company-name"
                      />
                      {/* iter263 — customer autocomplete */}
                      {customerSuggestionsOpen && !formData.client_id && customerSuggestions.length > 0 && (
                        <div className="absolute z-40 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-56 overflow-auto" data-testid="lead-customer-suggestions">
                          <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                            Existing customers
                          </div>
                          {customerSuggestions.map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => handlePickExistingCustomer(c)}
                              className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-slate-100 last:border-b-0"
                              data-testid={`lead-customer-suggestion-${c.id}`}
                            >
                              <div className="text-sm font-medium text-slate-800">{c.name}</div>
                              <div className="text-[11px] text-slate-500">
                                {[c.city, c.state].filter(Boolean).join(", ")}{c.contact_name ? ` — ${c.contact_name}` : ""}
                              </div>
                            </button>
                          ))}
                          {customerSearchLoading && (
                            <div className="px-3 py-2 text-xs text-slate-400">Searching…</div>
                          )}
                        </div>
                      )}
                      {/* Linked-existing indicator */}
                      {formData.client_id && (
                        <div className="mt-1 flex items-center gap-2" data-testid="lead-existing-customer-badge">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-medium border border-emerald-200">
                            <Check className="w-3 h-3" /> Existing customer
                          </span>
                          <button
                            type="button"
                            onClick={handleClearExistingCustomer}
                            className="text-[11px] text-slate-500 hover:text-red-600"
                            data-testid="lead-clear-existing-customer"
                          >
                            unlink
                          </button>
                        </div>
                      )}
                      {!formData.client_id && (formData.company_name || "").length >= 2 && !customerSearchLoading && customerSuggestions.length === 0 && customerSuggestionsOpen && (
                        <div className="mt-1 text-[11px] text-slate-500" data-testid="lead-no-match-hint">
                          No existing customer matches — a <span className="font-semibold">new customer</span> is created with this lead (customer # assigned later).
                        </div>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="lead_title">Lead / Project Name</Label>
                      <Input
                        id="lead_title"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="What we're chasing — e.g. RTU replacement, Bldg B"
                        data-testid="input-lead-title"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact_name">Contact Name *</Label>
                      <Input
                        id="contact_name"
                        value={formData.contact_name}
                        onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                        required
                        data-testid="input-contact-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        data-testid="input-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: formatPhoneNumber(e.target.value) })}
                        data-testid="input-phone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="status">Stage</Label>
                      <Select
                        value={formData.status}
                        onValueChange={(value) => setFormData({ ...formData, status: value })}
                      >
                        <SelectTrigger data-testid="select-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {statusOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="temperature">Temperature</Label>
                      <Select value={formData.temperature || "none"} onValueChange={(value) => setFormData({ ...formData, temperature: value === "none" ? "" : value })}>
                        <SelectTrigger data-testid="select-temperature"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">—</SelectItem>
                          {TEMPERATURES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="source">Lead Source</Label>
                      {isAddingSource ? (
                        <div className="flex gap-2">
                          <Input
                            value={newSourceName}
                            onChange={(e) => setNewSourceName(e.target.value)}
                            placeholder="New source name"
                            data-testid="input-new-source"
                          />
                          <Button type="button" size="sm" onClick={handleAddSource}>Add</Button>
                          <Button type="button" size="sm" variant="outline" onClick={() => setIsAddingSource(false)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <SearchableSelect
                          value={formData.source || "none"}
                          onValueChange={(value) => setFormData({ ...formData, source: value === "none" ? "" : value })}
                          options={leadSources.map(s => ({ value: s.name, label: s.name }))}
                          placeholder="Select source"
                          searchPlaceholder="Search sources..."
                          onAddNew={() => setIsAddingSource(true)}
                          addLabel="Add new source"
                          className="w-full"
                          data-testid="select-source"
                        />
                      )}
                    </div>
                    <div>
                      <Label htmlFor="estimated_value">Estimated Value</Label>
                      <Input
                        id="estimated_value"
                        value={formData.estimated_value ? `$${Number(formData.estimated_value).toLocaleString()}` : ""}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/[^0-9.]/g, "");
                          setFormData({ ...formData, estimated_value: parseFloat(raw) || 0 });
                        }}
                        placeholder="$0"
                        data-testid="input-estimated-value"
                      />
                    </div>
                    <div>
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        data-testid="input-city"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State</Label>
                      <Input
                        id="state"
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value.toUpperCase() })}
                        data-testid="input-state"
                      />
                    </div>
                    <div className="form-grid-full">
                      <Label htmlFor="notes">{editingLead ? "Add a note (optional)" : "Notes"}</Label>
                      <Textarea
                        id="notes"
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        rows={3}
                        placeholder={editingLead ? "Appends to this lead's note log — existing notes stay as they are" : "Free-form notes..."}
                        data-testid="input-notes"
                      />
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <Checkbox
                        id="immediate_action"
                        checked={formData.immediate_action}
                        onCheckedChange={(checked) => setFormData({ ...formData, immediate_action: !!checked })}
                        data-testid="checkbox-immediate-action"
                      />
                      <label htmlFor="immediate_action" className="text-sm font-medium cursor-pointer">Immediate Action Required</label>
                    </div>
                    <div>
                      <Label>Follow-Up Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start text-left font-normal h-9" data-testid="btn-form-followup-date">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {formData.follow_up_date ? format(formData.follow_up_date, 'MMM d, yyyy') : "Schedule follow-up"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={formData.follow_up_date}
                            onSelect={(date) => setFormData({ ...formData, follow_up_date: date })}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <Label>Follow-Up Note</Label>
                      <Input
                        value={formData.follow_up_note}
                        onChange={(e) => setFormData({ ...formData, follow_up_note: e.target.value })}
                        placeholder="e.g. Call to discuss proposal"
                        data-testid="input-form-followup-note"
                      />
                    </div>
                    <div>
                      <Label>Account Manager</Label>
                      {isAddingAM ? (
                        <div className="flex gap-2">
                          <Input value={newAMName} onChange={(e) => setNewAMName(e.target.value)} placeholder="Name" className="h-9 text-sm" autoFocus onKeyDown={(e) => e.key === "Enter" && handleAddAM()} data-testid="input-new-am" />
                          <Button type="button" size="sm" onClick={handleAddAM}>Add</Button>
                          <Button type="button" size="sm" variant="outline" onClick={() => { setIsAddingAM(false); setNewAMName(""); }}>Cancel</Button>
                        </div>
                      ) : (
                        <Select value={formData.account_manager} onValueChange={(v) => setFormData({ ...formData, account_manager: v === "__none__" ? "" : v })}>
                          <SelectTrigger className="h-9" data-testid="select-form-am"><SelectValue placeholder="Select AM" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">— None —</SelectItem>
                            {assignablePeople(accountManagers, formData.account_manager).map(am => <SelectItem key={am.id} value={am.name}>{am.name}</SelectItem>)}
                            <div className="border-t mt-1 pt-1 px-2 pb-1"><Button type="button" variant="ghost" size="sm" className="w-full text-xs" onClick={() => setIsAddingAM(true)} data-testid="btn-add-am">+ Add Account Manager</Button></div>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className={BTN.write} data-testid="btn-save-lead">
                      {editingLead ? "Update" : "Create"} lead
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

        <span className="ml-auto text-xs text-slate-500 num" data-testid="leads-count">
          {viewRows.length} {listView === "open" ? "" : `${listView} `}{viewRows.length === 1 ? "lead" : "leads"} · {money(viewTotal)}
        </span>
      </div>
      </StickyTop>

      <div className="data-table-container">
        {/* Table */}
        {(listView === "open" || listView === "archived") && (
        <Table className="table-fixed">
          <TableHeader className="thead-sticky">
            <TableRow>
              <TableHead className="whitespace-nowrap text-xs font-semibold">Customer</TableHead>
              <TableHead className="whitespace-nowrap text-xs"><ColHeader {...hdr} field="company_name" testId="leads-sort-company">Lead</ColHeader></TableHead>
              <TableHead className="whitespace-nowrap text-xs"><ColHeader {...hdr} field="contact_name" testId="leads-sort-contact">Contact</ColHeader></TableHead>
              <TableHead className="w-[92px] whitespace-nowrap text-xs font-semibold">Phone</TableHead>
              <TableHead className="w-[104px] whitespace-nowrap text-xs">
                <ColHeader {...hdr} field="status" testId="leads-sort-status" filter={
                  <HeaderFilter label={listView === "archived" ? "Status" : "Stage"} testId="filter-status"
                    options={listView === "archived" ? [{ value: "won", label: "Won" }, { value: "lost", label: "Lost" }] : STAGES.map(o => ({ value: o.value, label: o.label }))}
                    value={statusFilter === "all" ? "" : statusFilter} onChange={(v) => setStatusFilter(v || "all")} />
                }>{listView === "archived" ? "Status" : "Stage"}</ColHeader>
              </TableHead>
              <TableHead className="w-[52px] whitespace-nowrap text-center text-xs"><ColHeader {...hdr} field="temperature" testId="leads-sort-temp">Temp</ColHeader></TableHead>
              <TableHead className="w-[80px] whitespace-nowrap text-xs"><ColHeader {...hdr} field="source" testId="leads-sort-source">Source</ColHeader></TableHead>
              <TableHead className="w-[72px] whitespace-nowrap text-xs num"><ColHeader {...hdr} field="created_at" testId="leads-sort-created">Created</ColHeader></TableHead>
              <TableHead className="w-[92px] whitespace-nowrap text-xs num"><ColHeader {...hdr} field="estimated_value" testId="leads-sort-value">Est. Value</ColHeader></TableHead>
              <TableHead className="w-[64px] whitespace-nowrap text-xs"><ColHeader {...hdr} field="account_manager" testId="leads-sort-am">AM</ColHeader></TableHead>
              <TableHead className="w-[72px] whitespace-nowrap text-xs font-semibold">Follow Up</TableHead>
              <TableHead className="w-[64px] whitespace-nowrap text-center text-xs font-semibold">Actions</TableHead>
              <TableHead className="w-[56px] whitespace-nowrap text-center text-xs font-semibold">Notes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={13} className="text-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand mx-auto"></div>
                </TableCell>
              </TableRow>
            ) : sortedLeads.length === 0 ? (
              <TableRow>
                <TableCell colSpan={13} className="text-center py-8">
                  <div className="empty-state">
                    <p className="empty-state-title">{search.trim() ? "No open matches" : listView === "archived" ? "No archived leads" : "No leads found"}</p>
                    <p className="empty-state-text">{search.trim() ? (archivedRows.length ? "See archived matches below" : "Try a different search") : listView === "archived" ? "Won and lost leads will show here" : "Add your first lead to get started"}</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              sortedLeads.map((lead) => (
                <TableRow key={lead.id} className="table-row-hover" data-testid={`lead-row-${lead.id}`}>
                  <TableCell>
                    {hasCustomer(lead) ? (
                      <button type="button" title="Open the customer record"
                        className="block max-w-full truncate text-left text-xs font-medium text-slate-700 hover:text-brand hover:underline"
                        onClick={() => navigate("/clients", { state: { openClientId: lead.client_id || lead.converted_client_id, returnTo: "/leads" } })}
                        data-testid={`lead-customer-${lead.id}`}>
                        {lead.client_name || lead.company_name}
                      </button>
                    ) : isOpenStage(lead.status) ? (
                      <button type="button" title="No customer record yet — create one for this lead"
                        className={`${PILL_FIXED} bg-slate-100 text-slate-700 border-slate-200 hover:border-emerald-400 hover:text-emerald-700`}
                        onClick={() => openConvertDialog(lead)} data-testid={`lead-customer-${lead.id}`}>
                        + Customer
                      </button>
                    ) : (
                      <span className="text-xs text-slate-300" data-testid={`lead-customer-${lead.id}`}>—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <button className="block w-full truncate text-left text-xs font-medium text-brand hover:underline" onClick={() => handleEdit(lead)} data-testid={`lead-name-${lead.id}`}>
                      {lead.title || lead.company_name}
                    </button>
                  </TableCell>
                  <TableCell>
                    <div className="truncate text-xs text-slate-700" data-testid={`lead-contact-${lead.id}`}>{lead.contact_name || <span className="text-slate-300">—</span>}</div>
                    {lead.email && (
                      <a href={`mailto:${lead.email}`} className="block truncate text-[10px] text-slate-400 hover:text-brand hover:underline" onClick={(e) => e.stopPropagation()} data-testid={`lead-email-${lead.id}`}>
                        {lead.email}
                      </a>
                    )}
                  </TableCell>
                  <TableCell className="text-xs whitespace-nowrap">{lead.phone ? <a href={`tel:${lead.phone}`} className="text-slate-600 hover:text-brand hover:underline" data-testid={`lead-phone-${lead.id}`}>{lead.phone}</a> : <span className="text-slate-300">—</span>}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {isOpenStage(lead.status) ? (
                      <PillSelect value={lead.status} options={STAGES} onChange={(v) => handleInlineStatusChange(lead.id, v)} title="Change stage" className="!w-[90px] justify-center px-1.5"
                        testId={`lead-status-${lead.id}`} optionTestId={(v) => `lead-status-opt-${v}-${lead.id}`} />
                    ) : (
                      <span data-testid={`lead-status-${lead.id}`}><StagePill status={lead.status} fixed /></span>
                    )}
                  </TableCell>
                  <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                    <TempDot value={lead.temperature} onChange={(v) => handleInlineTempChange(lead.id, v)}
                      testId={`lead-temp-${lead.id}`} optionTestId={(v) => `lead-temp-opt-${v}-${lead.id}`} />
                  </TableCell>
                  <TableCell className="truncate text-xs text-slate-600">{lead.source || <span className="text-slate-300">—</span>}</TableCell>
                  <TableCell className="text-xs text-slate-500 num whitespace-nowrap">{shortDate(lead.created_at)}</TableCell>
                  <TableCell className="text-xs font-medium tabular-nums whitespace-nowrap">{money(lead.estimated_value)}</TableCell>
                  <TableCell className="text-xs text-slate-600">{lead.account_manager ? lead.account_manager.split(" ")[0] : <span className="text-slate-300">—</span>}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-7 px-1 text-xs" title="Set or change the follow-up date" data-testid={`lead-followup-${lead.id}`}>
                          {lead.next_follow_up && !lead.follow_up_complete ? (
                            <span className={new Date(lead.next_follow_up) < new Date() ? "text-red-600 font-medium" : "text-brand"}>
                              {shortDate(lead.next_follow_up)}
                            </span>
                          ) : lead.follow_up_complete ? (
                            <span className="text-emerald-600">Done</span>
                          ) : (
                            <span className="text-slate-400">Set date</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={lead.next_follow_up ? new Date(lead.next_follow_up) : undefined}
                          onSelect={async (date) => {
                            try {
                              await updateLeadFollowUp(lead.id, {
                                next_follow_up: date ? date.toISOString() : null,
                                follow_up_notes: lead.follow_up_notes || "",
                                follow_up_complete: false,
                              });
                              loadLeads();
                              toast.success("Follow-up date updated");
                            } catch { toast.error("Failed to update"); }
                          }}
                          initialFocus
                        />
                        {lead.next_follow_up && !lead.follow_up_complete && (
                          <div className="border-t px-3 py-2">
                            <button type="button" className={btn("write")} onClick={() => handleFollowUpComplete(lead)} data-testid={`lead-followup-done-${lead.id}`}>
                              <Check className="w-3.5 h-3.5" /> Mark done
                            </button>
                          </div>
                        )}
                      </PopoverContent>
                    </Popover>
                  </TableCell>
                  <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                    {isOpenStage(lead.status) && (
                      <div className="inline-flex items-center gap-1">
                        <button type="button" onClick={() => setWonConfirm(lead)}
                          title="Create the proposal and archive the lead."
                          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-emerald-600 hover:bg-emerald-100 hover:text-emerald-700 transition-colors"
                          data-testid={`lead-mark-won-${lead.id}`}>
                          <Check className="w-4 h-4" />
                        </button>
                        <button type="button" onClick={() => setLostConfirm(lead)} title="Lost"
                          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-red-500 hover:bg-red-100 hover:text-red-700 transition-colors"
                          data-testid={`lead-lost-${lead.id}`}>
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                    <LeadNotesCell lead={lead} onChanged={loadLeads} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        )}

        {/* Archived = Won + Lost in one list (toolbar "Archived" pill). While searching, archived matches also show under the open list. */}
        {listView === "open" && search.trim() && archivedRows.length > 0 && (
          <div className="border-t border-slate-200 mt-3" data-testid="archived-search-matches">
            <div className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Archived matches · {archivedRows.length}</div>
            <ArchivedLeadsTable rows={archivedRows} navigate={navigate} onRestore={restoreLead} />
          </div>
        )}
      </div>

      {/* Won confirm — existing-customer lead (a prospect confirms via the convert dialog below) */}
      <Dialog open={!!wonConfirm} onOpenChange={(open) => { if (!open) setWonConfirm(null); }}>
        <DialogContent className="max-w-sm" data-testid="lead-won-confirm">
          <DialogHeader>
            <DialogTitle>Mark as Won</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Mark <strong>{wonConfirm?.title || wonConfirm?.company_name}</strong> as <span className="font-semibold text-emerald-700">Won</span>? {wonConfirm?.proposal_id ? "The lead is archived and its proposal opens." : "A draft proposal is created for the customer, the lead is archived, and the proposal opens."}</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setWonConfirm(null)} data-testid="btn-cancel-won">Cancel</Button>
              <Button className={BTN.write} onClick={() => handleMarkWon(wonConfirm)} data-testid="btn-confirm-won">
                <Check className="w-4 h-4 mr-1.5" /> {wonConfirm?.proposal_id ? "Won — open proposal" : "Won — create proposal"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lost confirm */}
      <Dialog open={!!lostConfirm} onOpenChange={(open) => { if (!open) setLostConfirm(null); }}>
        <DialogContent className="max-w-sm" data-testid="lead-lost-confirm">
          <DialogHeader>
            <DialogTitle>Mark as Lost</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Mark <strong>{lostConfirm?.company_name}</strong> as <span className="font-semibold text-red-600">Lost</span>? It moves to the Lost list and can be restored from there.</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setLostConfirm(null)} data-testid="btn-cancel-lost">Cancel</Button>
              <Button variant="destructive" onClick={() => handleLost(lostConfirm.id)} data-testid="btn-confirm-lost">
                <X className="w-4 h-4 mr-1.5" /> Mark lost
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create customer — legacy leads without a customer record. Stage is untouched. */}
      <Dialog open={!!convertDialog} onOpenChange={(open) => !open && setConvertDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Create customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600"><strong>{convertDialog?.company_name}</strong> is added to the customer list and linked to this lead. A customer # is assigned later, when a job happens.</p>
            <div>
              <Label>Site Name</Label>
              <Input
                value={convertSiteName}
                onChange={(e) => setConvertSiteName(e.target.value)}
                placeholder="e.g. Main Office, Headquarters"
                data-testid="input-convert-site-name"
              />
            <p className="text-xs text-slate-400 mt-1">A primary site will be created with the lead&apos;s address.</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConvertDialog(null)}>Cancel</Button>
              <Button className={BTN.write} onClick={handleConvert} data-testid="btn-confirm-convert">
                <Plus className="w-4 h-4 mr-1.5" /> Create customer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
